import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { useButton } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';

/**
 * Big square-ish choice for the checkout: icon over label. `primary` fills it
 * in the button color; otherwise it is outlined. Same size either way so two
 * choices side by side read as equals.
 */
const Button = styled.button`
  appearance: none;
  min-width: 300px;
  min-height: 200px;
  padding: 28px 40px;
  border-radius: 32px;
  border: 2px solid var(--eposBorderColor);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.3px;
  background: transparent;
  color: var(--eposText);
  transition: transform 0.15s ease-out, opacity 0.2s ease-out;

  &.is-primary {
    border-color: var(--eposButton);
    background: var(--eposButton);
    color: var(--eposButtonText);
  }

  &.is-touching {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

interface Props {
  label: string;
  icon: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  testId?: string;
}

const ChoiceButton: FC<Props> = ({ label, icon, onClick, primary = false, disabled, testId }) => {
  const { touching, disabled: d, disabledToast, ...button } = useButton({ onClick, disabled });

  const className = [primary && 'is-primary', touching && 'is-touching'].filter(Boolean).join(' ');

  return (
    <Button
      {...button}
      disabled={disabled}
      className={className || undefined}
      data-testid={testId}>
      <Icon
        src={icon}
        color={primary ? 'var(--eposButtonText)' : 'var(--eposText)'}
        width="48px"
        height="48px"
      />
      {label}
    </Button>
  );
};

export default memo(ChoiceButton);
